import React, { useState } from "react";
import {
  Gift,
  CheckCircle2,
  Circle,
  User,
  Users,
  HelpCircle,
  ShoppingBag,
  ChevronDown,
  ChevronRight,
} from "lucide-react";

const INITIAL_GIFTS = [
  // ... (Keeping data the same, just hidden for brevity in logic) ...
  { id: "mom", name: "Mom", acquired: false, owner: "unassigned", notes: "" },
  {
    id: "bonny",
    name: "Aunt Bonny",
    acquired: false,
    owner: "unassigned",
    notes: "",
  },
  {
    id: "phil",
    name: "Uncle Phil",
    acquired: false,
    owner: "unassigned",
    notes: "",
  },
  {
    id: "luke",
    name: "Nephew (Luke)",
    acquired: false,
    owner: "unassigned",
    notes: "",
  },
  {
    id: "scout",
    name: "Scout (dog)",
    acquired: false,
    owner: "unassigned",
    notes: "",
  },
  {
    id: "jackson",
    name: "Jackson (dog)",
    acquired: false,
    owner: "unassigned",
    notes: "",
  },
  { id: "dad", name: "Dad", acquired: true, owner: "Ben", notes: "" },
  {
    id: "eric",
    name: "Brother (Eric)",
    acquired: true,
    owner: "Ben",
    notes: "",
  },
  {
    id: "christina",
    name: "Sister-in-law (Christina)",
    acquired: true,
    owner: "Melyssa",
    notes: "",
  },
  {
    id: "madelyn",
    name: "Niece (Madelyn)",
    acquired: true,
    owner: "Melyssa",
    notes: "",
  },
  {
    id: "jack",
    name: "Nephew (Jack)",
    acquired: true,
    owner: "Melyssa",
    notes: "",
  },
  {
    id: "partner",
    name: "Partner (Melyssa)",
    acquired: true,
    owner: "Ben",
    notes: "",
  },
  { id: "laura", name: "Laura", acquired: true, owner: "Ben", notes: "" },
  { id: "emma", name: "Emma", acquired: true, owner: "Ben", notes: "" },
];

const GiftControlCard = () => {
  const [isExpanded, setIsExpanded] = useState(false); // Default to collapsed
  const [gifts, setGifts] = useState(INITIAL_GIFTS);

  const toggleAcquired = (id) =>
    setGifts(
      gifts.map((g) => (g.id === id ? { ...g, acquired: !g.acquired } : g))
    );
  const cycleOwner = (id) => {
    const owners = ["unassigned", "Ben", "Melyssa", "Both"];
    setGifts(
      gifts.map((g) => {
        if (g.id === id) {
          const nextIndex = (owners.indexOf(g.owner) + 1) % owners.length;
          return { ...g, owner: owners[nextIndex] };
        }
        return g;
      })
    );
  };
  const updateNotes = (id, text) =>
    setGifts(gifts.map((g) => (g.id === id ? { ...g, notes: text } : g)));

  const total = gifts.length;
  const acquired = gifts.filter((g) => g.acquired).length;
  const remaining = total - acquired;

  const GiftRow = ({ gift }) => (
    <div
      className={`flex items-start gap-3 p-3 rounded-lg border transition-all ${
        gift.acquired
          ? "bg-stone-50 border-stone-100 opacity-60"
          : "bg-white border-amber-200 shadow-sm"
      }`}
    >
      <button
        onClick={() => toggleAcquired(gift.id)}
        className={`mt-1 flex-shrink-0 transition-colors ${
          gift.acquired
            ? "text-[#1F6F54]"
            : "text-stone-300 hover:text-[#1F6F54]"
        }`}
      >
        {gift.acquired ? (
          <CheckCircle2 className="w-5 h-5" />
        ) : (
          <Circle className="w-5 h-5" />
        )}
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-center mb-1">
          <span
            className={`font-semibold text-sm ${
              gift.acquired ? "text-stone-500 line-through" : "text-stone-800"
            }`}
          >
            {gift.name}
          </span>
          <button
            onClick={() => cycleOwner(gift.id)}
            className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full flex items-center gap-1 transition-colors ${
              gift.owner === "unassigned" ? "bg-amber-100 text-amber-700" : ""
            } ${gift.owner === "Ben" ? "bg-stone-100 text-stone-600" : ""} ${
              gift.owner === "Melyssa" ? "bg-stone-100 text-stone-600" : ""
            } ${gift.owner === "Both" ? "bg-blue-50 text-blue-600" : ""}`}
          >
            {gift.owner === "unassigned" && <HelpCircle className="w-3 h-3" />}
            {gift.owner === "Ben" && <User className="w-3 h-3" />}
            {gift.owner === "Melyssa" && <User className="w-3 h-3" />}
            {gift.owner === "Both" && <Users className="w-3 h-3" />}
            {gift.owner === "unassigned" ? "Assign" : gift.owner}
          </button>
        </div>
        <input
          value={gift.notes}
          onChange={(e) => updateNotes(gift.id, e.target.value)}
          placeholder={gift.acquired ? "Done." : "Idea / Status..."}
          className="w-full bg-transparent text-xs text-stone-600 placeholder:text-stone-300 focus:outline-none focus:bg-stone-50 rounded px-1 -ml-1 py-0.5"
        />
      </div>
    </div>
  );

  return (
    <div className="mb-6 border-l-4 border-amber-400 bg-white rounded-r-xl shadow-sm overflow-hidden transition-all">
      {/* HEADER (CLICK TO TOGGLE) */}
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        className="p-5 border-b border-stone-100 bg-amber-50/30 cursor-pointer hover:bg-amber-50/50 transition-colors flex justify-between items-center"
      >
        <div>
          <div className="flex items-center gap-2 text-amber-600 mb-1">
            <Gift className="w-4 h-4" />
            <span className="text-xs font-bold uppercase tracking-widest">
              Shared Control Card
            </span>
          </div>
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-bold text-stone-800">
              🎁 Holiday Gifts & Coverage
            </h2>
            <div className="text-xs font-bold text-[#1F6F54] bg-[#EAF4EF] px-2 py-1 rounded-full">
              {acquired} of {total} Covered
            </div>
          </div>
        </div>

        {isExpanded ? (
          <ChevronDown className="w-5 h-5 text-stone-400" />
        ) : (
          <ChevronRight className="w-5 h-5 text-stone-400" />
        )}
      </div>

      {/* EXPANDABLE CONTENT */}
      {isExpanded && (
        <div className="p-4 space-y-6 animate-in slide-in-from-top-2 duration-200">
          {remaining > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-3 text-amber-600">
                <ShoppingBag className="w-4 h-4" />
                <h3 className="text-xs font-bold uppercase tracking-widest">
                  Still To Purchase
                </h3>
              </div>
              <div className="space-y-2">
                {gifts
                  .filter((g) => !g.acquired)
                  .map((gift) => (
                    <GiftRow key={gift.id} gift={gift} />
                  ))}
              </div>
            </section>
          )}

          <section>
            <div className="flex items-center gap-2 mb-3 text-[#1F6F54]">
              <CheckCircle2 className="w-4 h-4" />
              <h3 className="text-xs font-bold uppercase tracking-widest">
                Covered
              </h3>
            </div>
            <div className="space-y-2">
              {gifts
                .filter((g) => g.acquired)
                .map((gift) => (
                  <GiftRow key={gift.id} gift={gift} />
                ))}
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default GiftControlCard;
